import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import SocialShare from '@/components/SocialShare';
import ReadingProgress from '@/components/ReadingProgress';
import RelatedArticles from '@/components/RelatedArticles';
import NewsletterPopup from '@/components/NewsletterPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  useEffect(() => {
    if (id) {
      axios.post(`${API}/analytics/view/${id}`).catch(() => {});
    }
  }, [id]);

  const trackReferralClick = (blogId) => {
    axios.post(`${API}/analytics/click/${blogId}`).catch(() => {});
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderContent = (content) => {
    if (!content) return null;

    const lines = content.split('\n');
    const elements = [];
    let tableRows = [];
    let inTable = false;
    let isHeaderRow = true;

    const flushTable = () => {
      if (tableRows.length > 0) {
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto my-8">
            <table className="w-full border-collapse" style={{ borderColor: '#E2E8F0' }}>
              <tbody>
                {tableRows.map((row, ri) => (
                  <tr key={ri} className={ri === 0 ? '' : ''} style={ri === 0 ? { backgroundColor: '#0A0F1A' } : { backgroundColor: ri % 2 === 0 ? '#F8FAFC' : '#FFFFFF' }}>
                    {row.map((cell, ci) => {
                      const Tag = ri === 0 ? 'th' : 'td';
                      return (
                        <Tag
                          key={ci}
                          className="px-4 py-3 text-left text-sm border"
                          style={{
                            borderColor: '#E2E8F0',
                            color: ri === 0 ? '#FFFFFF' : '#475569',
                            fontWeight: ri === 0 ? 600 : 400,
                            fontFamily: ri === 0 ? 'Outfit' : 'Manrope',
                          }}
                        >
                          {cell.trim()}
                        </Tag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
        isHeaderRow = true;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith('|') && line.endsWith('|')) {
        if (line.match(/^\|[\s-|]+\|$/)) {
          continue;
        }
        const cells = line.split('|').filter((c) => c.trim() !== '');
        tableRows.push(cells);
        inTable = true;
        continue;
      }

      if (inTable) {
        flushTable();
      }

      if (line.trim() === '') {
        elements.push(<div key={`spacer-${i}`} className="h-4" />);
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(
          <h2
            key={`h2-${i}`}
            className="text-2xl sm:text-3xl font-semibold mt-12 mb-4"
            style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
          >
            {line.replace('## ', '')}
          </h2>
        );
        continue;
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h3
            key={`h3-${i}`}
            className="text-xl sm:text-2xl font-semibold mt-8 mb-3"
            style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
          >
            {line.replace('### ', '')}
          </h3>
        );
        continue;
      }

      if (line.startsWith('- **')) {
        const match = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)$/);
        if (match) {
          elements.push(
            <div key={`li-${i}`} className="flex items-start gap-3 my-2 ml-4">
              <span style={{ color: '#FF5722', flexShrink: 0, marginTop: '6px' }}>&#9679;</span>
              <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
                <strong style={{ color: '#0A0F1A' }}>{match[1]}:</strong> {match[2]}
              </p>
            </div>
          );
          continue;
        }
      }

      if (line.match(/^\d+\.\s/)) {
        const content = line.replace(/^\d+\.\s/, '');
        elements.push(
          <div key={`ol-${i}`} className="flex items-start gap-3 my-2 ml-4">
            <span className="font-semibold" style={{ color: '#FF5722', flexShrink: 0, minWidth: '24px' }}>
              {line.match(/^(\d+)\./)[1]}.
            </span>
            <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
              {renderInlineFormatting(content)}
            </p>
          </div>
        );
        continue;
      }

      if (line.startsWith('*Disclaimer')) {
        elements.push(
          <div
            key={`disc-${i}`}
            className="mt-8 p-6 border-l-4"
            style={{ borderColor: '#FF5722', backgroundColor: '#FFF4E6' }}
          >
            <p className="text-sm italic" style={{ color: '#475569' }}>
              {line.replace(/^\*/, '').replace(/\*$/, '')}
            </p>
          </div>
        );
        continue;
      }

      elements.push(
        <p key={`p-${i}`} className="text-base leading-relaxed my-2" style={{ color: '#475569' }}>
          {renderInlineFormatting(line)}
        </p>
      );
    }

    if (inTable) flushTable();

    return elements;
  };

  const renderInlineFormatting = (text) => {
    const parts = [];
    let remaining = text;
    let keyIdx = 0;

    while (remaining.length > 0) {
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);

      let firstMatch = null;
      let matchType = null;

      if (linkMatch && (!boldMatch || linkMatch.index < boldMatch.index)) {
        firstMatch = linkMatch;
        matchType = 'link';
      } else if (boldMatch) {
        firstMatch = boldMatch;
        matchType = 'bold';
      }

      if (!firstMatch) {
        parts.push(<span key={keyIdx++}>{remaining}</span>);
        break;
      }

      if (firstMatch.index > 0) {
        parts.push(<span key={keyIdx++}>{remaining.substring(0, firstMatch.index)}</span>);
      }

      if (matchType === 'link') {
        const linkUrl = firstMatch[2];
        const isReferral = linkUrl.includes('aonelink.in') || linkUrl.includes('angelone');
        parts.push(
          <a
            key={keyIdx++}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
            style={{ color: '#FF5722' }}
            data-testid="blog-inline-link"
            onClick={() => { if (isReferral) trackReferralClick(id); }}
          >
            {firstMatch[1]}
          </a>
        );
      } else {
        parts.push(
          <strong key={keyIdx++} style={{ color: '#0A0F1A' }}>
            {firstMatch[1]}
          </strong>
        );
      }

      remaining = remaining.substring(firstMatch.index + firstMatch[0].length);
    }

    return parts;
  };

  if (isLoading) {
    return (
      <div className="py-32 text-center">
        <p className="text-base" style={{ color: '#475569' }}>Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="py-32 text-center">
        <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>
          Article Not Found
        </h2>
        <Link to="/blog" className="text-[#FF5722] font-medium">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div>
      <ReadingProgress />
      <section className="py-16 md:py-24" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium mb-8 hover:text-[#FF5722] transition-colors"
            style={{ color: '#475569' }}
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <span
              className="text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1"
              style={{ backgroundColor: '#FFF4E6', color: '#FF5722' }}
            >
              {post.category}
            </span>
            <div className="flex items-center gap-1 text-xs" style={{ color: '#94A3B8' }}>
              <Clock size={14} />
              {post.read_time}
            </div>
            <div className="flex items-center gap-1 text-xs" style={{ color: '#94A3B8' }}>
              <Calendar size={14} />
              {formatDate(post.published_date)}
            </div>
          </div>

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-bold mb-8"
            style={{ fontFamily: 'Outfit', color: '#0A0F1A', lineHeight: '1.15' }}
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>

          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-72 md:h-96 object-cover mb-12"
            data-testid="blog-post-image"
          />

          <article className="prose max-w-none" data-testid="blog-post-content">
            {renderContent(post.content)}
          </article>

          <div className="mt-12 pt-8 flex items-center justify-between" style={{ borderTop: '1px solid #E2E8F0' }}>
            <SocialShare title={post.title} url={window.location.href} />
          </div>

          <div className="mt-12 p-8 text-center" style={{ backgroundColor: '#0A0F1A' }}>
            <h3
              className="text-xl sm:text-2xl font-semibold mb-4"
              style={{ fontFamily: 'Outfit', color: '#FFFFFF' }}
            >
              Ready to Start Your Investment Journey?
            </h3>
            <p className="text-base mb-6" style={{ color: '#94A3B8' }}>
              Open your free Demat account with Angel One today
            </p>
            <Link
              to="/brokerage-plans"
              data-testid="blog-post-cta"
              className="btn-primary inline-flex items-center gap-2"
              style={{
                backgroundColor: '#FF5722',
                color: 'white',
                fontWeight: 600,
                padding: '1rem 2rem',
                textDecoration: 'none',
              }}
              onClick={() => trackReferralClick(id)}
            >
              Open Free Account
              <ArrowRight size={20} />
            </Link>
          </div>

          <RelatedArticles blogId={id} />
        </div>
      </section>
      <NewsletterPopup />
    </div>
  );
};

export default BlogPost;
